// Fixed BlockToolbox.tsx - TSL v1.0 Compliant
// Removed invalid block types and fixed structure

import React, { useState, useMemo } from 'react';
import type { Block } from '../../types';
import { BlockType, BLOCK_COLORS } from '../../types';
import { fundamentalsDimensionGate } from '../../config/fundamentalsDimensionFlag';
import { FUNDAMENTAL_TEMPLATES, FUNDAMENTAL_HELPER_NOTE, buildFundamentalExpressionBlock } from './fundamentalTemplates';
import { CustomBlockLibraryButton } from './CustomBlockLibraryGallery';
import { LibraryProvenanceWarnings } from './LibraryProvenanceWarnings';
import { LegacyCodeBlockBanner } from './LegacyCodeBlockBanner';
import { EvidenceImportBanner } from './EvidenceImportBanner';
import { TslBlockStudio } from './TslBlockStudio';
import { useBlockLibraryGate } from '../../config/useBlockLibraryGate';
import { Boxes } from 'lucide-react';
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  Ban,
  BarChart2,
  Filter,
  Target,
  TrendingUp,
  Activity,
  Brain,
  Code,
  Search,
  ChevronDown,
  ChevronRight,
  Settings,
  Database,
  Clock,
  BarChart3,
  Calculator,
  Workflow,
  AlertTriangle,
  Eye,
  BarChart,
  TrendingDown,
} from 'lucide-react';

interface BlockToolboxProps {
  onDragStart: (type: BlockType, e: React.DragEvent) => void;
  // True while the builder is still resolving a persisted templateId (lifecycle redirect pending) — the
  // legacy-migration banner is suppressed so a retired payload never flashes before the successor loads.
  resolvingTemplate?: boolean;
  readonly?: boolean;
  showAdvancedBlocks?: boolean;
  // Fundamentals dimension (flag-gated): insert a preconfigured EXPRESSION block
  // (no new BlockType). Absent → the Fundamentals quick-add group is hidden.
  onQuickAddBlock?: (block: Block) => void;
}

interface ToolboxCategory {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  items: ToolboxItem[];
  collapsed?: boolean;
  advanced?: boolean;
}

interface ToolboxItem {
  type: BlockType;
  name: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  complexity: 'beginner' | 'intermediate' | 'advanced';
  tags: string[];
  new?: boolean;
  beta?: boolean;
  popular?: boolean;
}

const BlockToolbox: React.FC<BlockToolboxProps> = ({
  onDragStart,
  onQuickAddBlock,
  resolvingTemplate = false,
  readonly = false,
  showAdvancedBlocks = false,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(
    new Set()
  );
  const [selectedComplexity, setSelectedComplexity] = useState<
    'all' | 'beginner' | 'intermediate' | 'advanced'
  >('all');
  const [showOnlyPopular, setShowOnlyPopular] = useState(false);

  // TSL v1.0 Compliant Categories - Fixed invalid block types
  const categories: ToolboxCategory[] = [
    {
      id: 'core',
      name: 'TSL Strategy Foundation',
      description: 'Essential blocks for strategy configuration (TSL Core)',
      icon: <Settings className="w-5 h-5" />,
      color: 'blue',
      items: [
        {
          type: BlockType.STRATEGY_CONFIG,
          name: 'Strategy Configuration',
          description:
            'TSL Strategy foundation block - configures core parameters (standalone, no connections)',
          icon: <Settings className="w-5 h-5" />,
          color: BLOCK_COLORS[BlockType.STRATEGY_CONFIG],
          complexity: 'beginner',
          tags: ['core', 'required', 'config', 'standalone', 'tsl'],
          popular: true,
        },
        {
          type: BlockType.PRICE_DATA,
          name: 'Price Data Override',
          description:
            'TSL Price data source for multi-timeframe and cross-asset analysis',
          icon: <Database className="w-5 h-5" />,
          color: BLOCK_COLORS[BlockType.PRICE_DATA],
          complexity: 'intermediate',
          tags: ['data', 'price', 'ohlcv', 'multi-timeframe', 'tsl'],
        },
      ],
    },
    {
      id: 'indicators',
      name: 'TSL Technical Indicators',
      description:
        'Technical analysis indicators (must flow through logic blocks)',
      icon: <TrendingUp className="w-5 h-5" />,
      color: 'green',
      items: [
        {
          type: BlockType.MOVING_AVERAGE,
          name: 'Moving Average',
          description:
            'TSL Moving averages (SMA, EMA, WMA) → Logic Block Required',
          icon: <TrendingUp className="w-5 h-5" />,
          color: BLOCK_COLORS[BlockType.MOVING_AVERAGE],
          complexity: 'beginner',
          tags: ['indicator', 'trend', 'ma', 'tsl'],
          popular: true,
        },
        {
          type: BlockType.MOMENTUM_OSCILLATOR,
          name: 'Momentum & Oscillators',
          description:
            'TSL Momentum indicators and oscillators (RSI, MACD, Stochastic) → Logic Block Required',
          icon: <Activity className="w-5 h-5" />,
          color: BLOCK_COLORS[BlockType.MOMENTUM_OSCILLATOR],
          complexity: 'beginner',
          tags: ['indicator', 'momentum', 'oscillator', 'rsi', 'macd', 'tsl'],
          popular: true,
        },
        {
          type: BlockType.VOLATILITY,
          name: 'Volatility Indicators',
          description:
            'TSL Volatility indicators (ATR, Bollinger Bands) → Logic Block Required',
          icon: <BarChart3 className="w-5 h-5" />,
          color: BLOCK_COLORS[BlockType.VOLATILITY],
          complexity: 'intermediate',
          tags: ['indicator', 'volatility', 'bands', 'tsl'],
        },
        {
          type: BlockType.VOLUME,
          name: 'Volume Indicators',
          description:
            'TSL Volume-based indicators (OBV, VWAP) → Logic Block Required',
          icon: <BarChart2 className="w-5 h-5" />,
          color: BLOCK_COLORS[BlockType.VOLUME],
          complexity: 'intermediate',
          tags: ['indicator', 'volume', 'flow', 'tsl'],
        },
        {
          type: BlockType.TREND,
          name: 'Trend Indicators',
          description:
            'TSL Trend direction and strength (ADX, Ichimoku) → Logic Block Required',
          icon: <TrendingUp className="w-5 h-5" />,
          color: BLOCK_COLORS[BlockType.TREND],
          complexity: 'intermediate',
          tags: ['indicator', 'trend', 'direction', 'tsl'],
        },
      ],
    },
    {
      id: 'logic',
      name: 'TSL Logic & Conditions',
      description:
        'Logical operations and condition evaluation (TSL core logic)',
      icon: <Calculator className="w-5 h-5" />,
      color: 'purple',
      items: [
        {
          type: BlockType.EXPRESSION,
          name: 'Expression',
          description:
            'TSL Expression block - compare values and create conditional logic',
          icon: <Calculator className="w-5 h-5" />,
          color: BLOCK_COLORS[BlockType.EXPRESSION],
          complexity: 'beginner',
          tags: ['logic', 'comparison', 'condition', 'tsl'],
          popular: true,
        },
        {
          type: BlockType.LOGIC_GATE,
          name: 'Logic Gate',
          description:
            'TSL Logic Gate - combine signals with AND, OR, NOT operations',
          icon: <Workflow className="w-5 h-5" />,
          color: BLOCK_COLORS[BlockType.LOGIC_GATE],
          complexity: 'intermediate',
          tags: ['logic', 'gate', 'boolean', 'tsl'],
          popular: true,
        },
      ],
    },
    {
      id: 'patterns',
      name: 'TSL Pattern Recognition',
      description:
        'Chart patterns and candlestick formations (3 separate TSL blocks)',
      icon: <Eye className="w-5 h-5" />,
      color: 'pink',
      items: [
        {
          type: BlockType.CHART_PATTERNS,
          name: 'Chart Patterns',
          description:
            'TSL Chart patterns (triangles, flags, reversals) → Logic Block Required',
          icon: <BarChart className="w-5 h-5" />,
          color: BLOCK_COLORS[BlockType.CHART_PATTERNS],
          complexity: 'intermediate',
          tags: ['pattern', 'chart', 'technical', 'geometry', 'tsl'],
        },
        {
          type: BlockType.CANDLESTICK_PATTERNS,
          name: 'Candlestick Patterns',
          description:
            'TSL Japanese candlestick patterns → Logic Block Required',
          icon: <TrendingDown className="w-5 h-5" />,
          color: BLOCK_COLORS[BlockType.CANDLESTICK_PATTERNS],
          complexity: 'beginner',
          tags: ['pattern', 'candlestick', 'japanese', 'reversal', 'tsl'],
          popular: true,
        },
      ],
    },
    {
      id: 'filters',
      name: 'TSL Market Filters',
      description:
        'Filter trades based on market conditions → Logic Block Required',
      icon: <Filter className="w-5 h-5" />,
      color: 'amber',
      items: [
        {
          type: BlockType.TIME_FILTER,
          name: 'Time Filter',
          description:
            'TSL Time filter - filter by sessions and schedules → Logic Block Required',
          icon: <Clock className="w-5 h-5" />,
          color: BLOCK_COLORS[BlockType.TIME_FILTER],
          complexity: 'beginner',
          tags: ['filter', 'time', 'session', 'tsl'],
          popular: true,
        },
      ],
    },
    {
      id: 'trading',
      name: 'TSL Trading Signals',
      description: 'Entry and exit signal generation (TSL terminal blocks)',
      icon: <ArrowDownToLine className="w-5 h-5" />,
      color: 'emerald',
      items: [
        {
          type: BlockType.ENTRY,
          name: 'Entry Signal',
          description:
            'TSL Entry terminal block - define market entry conditions',
          icon: <ArrowDownToLine className="w-5 h-5" />,
          color: BLOCK_COLORS[BlockType.ENTRY],
          complexity: 'beginner',
          tags: ['trading', 'entry', 'signal', 'terminal', 'tsl'],
          popular: true,
        },
        {
          type: BlockType.EXIT,
          name: 'Exit Signal',
          description:
            'TSL Exit terminal block - define market exit conditions',
          icon: <ArrowUpFromLine className="w-5 h-5" />,
          color: BLOCK_COLORS[BlockType.EXIT],
          complexity: 'beginner',
          tags: ['trading', 'exit', 'signal', 'terminal', 'tsl'],
          popular: true,
        },
      ],
    },
    {
      id: 'risk',
      name: 'TSL Risk Management',
      description: 'Position sizing and risk control (TSL terminal blocks)',
      icon: <Ban className="w-5 h-5" />,
      color: 'red',
      items: [
        {
          type: BlockType.STOP_LOSS,
          name: 'Stop Loss',
          description:
            'TSL Stop Loss terminal block - protect against adverse movements',
          icon: <Ban className="w-5 h-5" />,
          color: BLOCK_COLORS[BlockType.STOP_LOSS],
          complexity: 'beginner',
          tags: ['risk', 'stop', 'protection', 'terminal', 'tsl'],
          popular: true,
        },
        {
          type: BlockType.TAKE_PROFIT,
          name: 'Take Profit',
          description:
            'TSL Take Profit terminal block - secure profits at target levels',
          icon: <Target className="w-5 h-5" />,
          color: BLOCK_COLORS[BlockType.TAKE_PROFIT],
          complexity: 'beginner',
          tags: ['risk', 'profit', 'target', 'terminal', 'tsl'],
          popular: true,
        },
      ],
    },
    {
      id: 'custom',
      name: 'Custom Blocks',
      description: 'TSL Custom blocks (indicator, pattern, analysis, filter, or signal generator) → Logic Block Required',
      icon: <Code className="w-5 h-5" />,
      color: 'purple',
      items: [
        {
          type: BlockType.CUSTOM,
          name: 'Custom Block',
          description:
            'TSL Custom blocks (indicator, pattern, analysis, filter, or signal generator) → Logic Block Required',
          icon: <Code className="w-5 h-5" />,
          color: BLOCK_COLORS[BlockType.CUSTOM],
          complexity: 'advanced',
          tags: ['custom', 'javascript', 'code', 'indicator', 'pattern', 'analysis', 'filter', 'signal', 'tsl'],
        },
      ],
    },
  ];

  // Filter categories based on search and settings
  const filteredCategories = useMemo(() => {
    return categories
      .filter((category) => {
        if (category.advanced && !showAdvancedBlocks) return false;

        const filteredItems = category.items.filter((item) => {
          if (searchQuery) {
            const query = searchQuery.toLowerCase();
            const matchesSearch =
              item.name.toLowerCase().includes(query) ||
              item.description.toLowerCase().includes(query) ||
              item.tags.some((tag) => tag.toLowerCase().includes(query));
            if (!matchesSearch) return false;
          }

          if (
            selectedComplexity !== 'all' &&
            item.complexity !== selectedComplexity
          ) {
            return false;
          }

          if (showOnlyPopular && !item.popular) {
            return false;
          }

          return true;
        });

        return filteredItems.length > 0;
      })
      .map((category) => ({
        ...category,
        items: category.items.filter((item) => {
          if (searchQuery) {
            const query = searchQuery.toLowerCase();
            const matchesSearch =
              item.name.toLowerCase().includes(query) ||
              item.description.toLowerCase().includes(query) ||
              item.tags.some((tag) => tag.toLowerCase().includes(query));
            if (!matchesSearch) return false;
          }

          if (
            selectedComplexity !== 'all' &&
            item.complexity !== selectedComplexity
          ) {
            return false;
          }

          if (showOnlyPopular && !item.popular) {
            return false;
          }

          return true;
        }),
      }));
  }, [
    categories,
    searchQuery,
    selectedComplexity,
    showOnlyPopular,
    showAdvancedBlocks,
  ]);

  const handleDragStart = (type: BlockType, e: React.DragEvent) => {
    if (readonly) {
      e.preventDefault();
      return;
    }

    e.stopPropagation();
    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData('application/x-block-type', type);
    e.dataTransfer.setData('text/plain', type);

    onDragStart(type, e);
  };

  const toggleCategory = (categoryId: string) => {
    setCollapsedCategories((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  const getComplexityColor = (complexity: string) => {
    switch (complexity) {
      case 'beginner':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'intermediate':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'advanced':
        return 'bg-red-100 text-red-800 border-red-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  return (
    <div className="bg-white border-r border-gray-200 w-80 overflow-y-auto flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center mb-3">
          <Brain className="w-5 h-5 mr-2 text-blue-600" />
          <h3 className="text-lg font-medium text-gray-800">
            TSL Strategy Blocks
          </h3>
        </div>
        <div className="text-xs text-blue-600 mb-3 p-2 bg-blue-50 rounded border border-blue-200">
          🔷 Trading Strategy Language v1.0 - Structured Signal Flow
        </div>

        {/* Custom Block Library (curated + community) + author a Safe-TSL block. Hidden when killed. */}
        <div className="mb-3 space-y-2">
          <div className="flex items-center gap-2">
            <CustomBlockLibraryButton />
            <TslBlockStudioButton />
          </div>
          <LibraryProvenanceWarnings />
          <LegacyCodeBlockBanner suppressed={resolvingTemplate} />
          <EvidenceImportBanner />
        </div>

        {/* Search */}
        <div className="relative mb-3">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search TSL blocks..."
            className="w-full pl-8 pr-3 py-2 text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-2.5" />
        </div>

        {/* Filters */}
        <div className="space-y-2">
          <div className="flex flex-wrap gap-1">
            {(['all', 'beginner', 'intermediate', 'advanced'] as const).map(
              (complexity) => (
                <button
                  key={complexity}
                  onClick={() => setSelectedComplexity(complexity)}
                  className={`px-2 py-1 text-xs rounded-md border transition-colors ${
                    selectedComplexity === complexity
                      ? 'bg-blue-100 text-blue-800 border-blue-300'
                      : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {complexity === 'all'
                    ? 'All Levels'
                    : complexity.charAt(0).toUpperCase() + complexity.slice(1)}
                </button>
              )
            )}
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="showPopular"
              checked={showOnlyPopular}
              onChange={(e) => setShowOnlyPopular(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="showPopular" className="ml-2 text-sm text-gray-600">
              Popular TSL blocks only
            </label>
          </div>
        </div>
      </div>

      {/* Categories and blocks */}
      <div className="flex-1 p-4 space-y-4">
        {/* Fundamentals dimension — flag-gated quick-add group. When the flag is OFF
            (or onQuickAddBlock is not wired) NOTHING here renders. Each card inserts a
            normal EXPRESSION block preconfigured with a Fundamental (SF1) feature operand
            — no new BlockType. The operand dropdown in the editor still works as before. */}
        {onQuickAddBlock && fundamentalsDimensionGate().enabled && (
          <div className="space-y-2 rounded-md border border-emerald-200 bg-emerald-50/60 p-3">
            <div className="flex items-center gap-2">
              <Calculator className="w-4 h-4 text-emerald-600" />
              <div className="font-medium text-gray-800">Fundamentals (SF1)</div>
            </div>
            <div className="text-[11px] text-gray-500">{FUNDAMENTAL_HELPER_NOTE}</div>
            <div className="grid grid-cols-2 gap-2">
              {FUNDAMENTAL_TEMPLATES.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  disabled={readonly}
                  title={t.hint}
                  onClick={() => onQuickAddBlock(buildFundamentalExpressionBlock(t))}
                  className="rounded-md border border-emerald-300 bg-white px-2 py-1.5 text-left text-xs font-medium text-emerald-900 hover:bg-emerald-100 disabled:opacity-50"
                >
                  {t.label}
                </button>
              ))}
            </div>
            <div className="text-[10px] text-gray-400">Inserts a preconfigured Expression block · combine with a Logic Gate (AND).</div>
          </div>
        )}
        {filteredCategories.map((category) => {
          const isCollapsed = collapsedCategories.has(category.id);

          return (
            <div key={category.id} className="space-y-2">
              {/* Category header */}
              <button
                onClick={() => toggleCategory(category.id)}
                className="w-full flex items-center justify-between p-2 rounded-md hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center">
                  <div className={`mr-2 text-${category.color}-600`}>
                    {category.icon}
                  </div>
                  <div className="text-left">
                    <div className="font-medium text-gray-800">
                      {category.name}
                    </div>
                    <div className="text-xs text-gray-500">
                      {category.description}
                    </div>
                  </div>
                </div>
                <div className="flex items-center">
                  <span className="text-xs text-gray-400 mr-2">
                    {category.items.length}
                  </span>
                  {isCollapsed ? (
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                  )}
                </div>
              </button>

              {/* Category items */}
              {!isCollapsed && (
                <div className="space-y-2 ml-2">
                  {category.items.map((item) => (
                    <div
                      key={item.type}
                      draggable={!readonly}
                      onDragStart={(e) => handleDragStart(item.type, e)}
                      className={`group p-3 rounded-md border transition-all duration-200 ${
                        readonly
                          ? 'cursor-not-allowed opacity-50'
                          : 'cursor-grab hover:shadow-md active:shadow-inner border-gray-200 hover:border-gray-300'
                      }`}
                      style={{
                        backgroundColor: item.color + '10',
                        borderColor: item.color + '30',
                      }}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center flex-1">
                          <div
                            className="mr-2 p-1 rounded"
                            style={{
                              backgroundColor: item.color + '20',
                              color: item.color,
                            }}
                          >
                            {item.icon}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-1">
                              <span className="font-medium text-gray-800 text-sm truncate">
                                {item.name}
                              </span>
                              {item.beta && (
                                <span className="px-1.5 py-0.5 text-xs bg-orange-100 text-orange-800 rounded-full">
                                  BETA
                                </span>
                              )}
                              {item.popular && (
                                <span className="px-1.5 py-0.5 text-xs bg-blue-100 text-blue-800 rounded-full">
                                  ★
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        <span
                          className={`px-2 py-0.5 text-xs rounded-full border ${getComplexityColor(
                            item.complexity
                          )}`}
                        >
                          {item.complexity.charAt(0).toUpperCase()}
                        </span>
                      </div>

                      <p className="text-xs text-gray-600 leading-relaxed mb-2">
                        {item.description}
                      </p>

                      {/* Tags */}
                      <div className="flex flex-wrap gap-1">
                        {item.tags.slice(0, 3).map((tag, index) => (
                          <span
                            key={index}
                            className="px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600 rounded"
                          >
                            {tag}
                          </span>
                        ))}
                        {item.tags.length > 3 && (
                          <span className="px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                            +{item.tags.length - 3}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {filteredCategories.length === 0 && (
          <div className="text-center py-8">
            <Search className="w-12 h-12 text-gray-400 mx-auto mb-2" />
            <h3 className="text-lg font-medium text-gray-800 mb-1">
              No TSL blocks found
            </h3>
            <p className="text-gray-600">
              Try adjusting your search or filters
            </p>
          </div>
        )}
      </div>

      {/* Footer with TSL info */}
      <div className="border-t border-gray-200 p-4 bg-gray-50">
        {readonly ? (
          <div className="flex items-center text-orange-600">
            <AlertTriangle className="w-4 h-4 mr-2" />
            <span className="text-sm">Read-only mode</span>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center text-blue-600">
              <Brain className="w-4 h-4 mr-2" />
              <span className="text-sm font-medium">TSL v1.0 Quick Guide</span>
            </div>
            <ul className="text-xs text-gray-600 space-y-1">
              <li>• Start with Strategy Configuration (standalone)</li>
              <li>• Indicators/Patterns → Logic Blocks → Terminal Blocks</li>
              <li>• Use Expression/Logic Gate for signal processing</li>
              <li>• Terminal blocks: Entry, Exit, Stop Loss, Take Profit</li>
              <li>• Price Data blocks for multi-timeframe analysis</li>
            </ul>
            <div className="text-xs text-blue-600 mt-2 p-1 bg-blue-50 border border-blue-200 rounded">
              ✓ TSL enforces structured signal flow for reliable strategies
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Author a new public Safe-TSL block in the Studio (declarative; no JavaScript). Hidden when the library
// kill switch is on.
const TslBlockStudioButton: React.FC = () => {
  const gate = useBlockLibraryGate();
  const [open, setOpen] = React.useState(false);
  if (!gate.library.enabled) return null;
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50">
        <Boxes className="w-4 h-4" /> New TSL Block
      </button>
      {open && <TslBlockStudio onClose={() => setOpen(false)} />}
    </>
  );
};

export default BlockToolbox;
